import { isV1Url } from './v1-base';
import { stripLegacyBase, APP_PATHS, AppRoute } from './app-routes.enum';
import { ShareResource, isShareableId } from './share-routes';

/**
 * A resolved router destination: absolute `commands` (+ optional `queryParams`)
 * ready to hand to `Router.navigate`.
 */
export interface DeepLinkRoute {
  commands: readonly string[];
  queryParams?: Record<string, string>;
}

/**
 * v2-deep-link — the SINGLE resolver for a SERVER-AUTHORED deep link → a real /v2
 * destination.
 *
 * The backend authors notification/CTA links against a `/section/{sub}/{id}` path
 * shape (`/care/sessions/55`, `/community/posts/91`, `/profile/prescriptions`),
 * but the web app routes intra-page surfaces via `?screen=` query aliases — a raw
 * `navigateByUrl` on those paths would miss every one of them and fall through to the
 * 404 wildcard. This maps the documented link shapes onto the real web routes,
 * carrying the entity id through as the query param the target surface already reads
 * (`post`, `content`+`kind`), and returns `null` for anything unrecognised so the
 * caller can fall back to a coarser section route (or refuse to navigate at all
 * rather than dumping the viewer somewhere arbitrary).
 *
 * PREFIX-AGNOSTIC. The server's link templates were authored while the redesign was
 * mounted at `/v2`, and they still say `/v2/care/sessions/55`. Those rows already
 * exist in `custom_notifications` and the config is not ours to retro-edit, so the
 * legacy base is stripped ({@link stripLegacyBase}) and BOTH shapes resolve to the
 * same destination. Without that tolerance every notification authored to date would
 * 404 the moment the app moved to the root.
 *
 * Only INTERNAL links are honoured — an absolute `http(s)://` link, a `/v1` legacy
 * path, or junk returns `null` (a notification must never be able to steer the router
 * at will).
 *
 * Pure + SSR-safe: string work only, no platform or router access.
 */
export function resolveDeepLink(link: string | null | undefined): DeepLinkRoute | null {
  if (!link || typeof link !== 'string') return null;
  // Split the query/hash off before matching (the id lives in the PATH in every
  // documented link shape), then normalise away a legacy `/v2` base.
  const trimmed = link.trim();
  const path = stripLegacyBase(trimmed.split(/[?#]/)[0]!.trim());
  // Root-relative only. `//host` must be rejected EXPLICITLY, not left to fall through
  // the section switch: the browser reads a leading `//` as a scheme-relative ABSOLUTE
  // url, so it is an off-site destination wearing a path's clothes.
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  if (isV1Url(path)) return null; // a legacy `/v1/...` target is not ours to open

  const segs = path.split('/').filter(Boolean); // [<section>, <sub>, <id>]
  const section = (segs[0] ?? '').toLowerCase();
  const sub = (segs[1] ?? '').toLowerCase();
  const id = (segs[2] ?? '').trim();
  // The ONE query param a documented link carries: `?comment={id}` on a community
  // post link — the comment to scroll to + highlight inside the thread. Read with a
  // plain regex (no URL/URLSearchParams) to stay pure + SSR-safe on a relative path.
  const comment = (/[?&]comment=([^&#]+)/.exec(trimmed)?.[1] ?? '').trim();

  switch (section) {
    case '':
    case AppRoute.HOME:
      return { commands: [APP_PATHS.HOME] };

    case AppRoute.CARE:
      return resolveCareLink(sub, id);

    case AppRoute.KHOYEK:
      return { commands: [APP_PATHS.KHOYEK] };

    case AppRoute.SUBSCRIPTION:
      return { commands: [APP_PATHS.SUBSCRIPTION] };

    case AppRoute.PROFILE:
      // `/v2/profile/prescriptions` → the profile prescriptions screen; else the hub.
      return sub === 'prescriptions'
        ? { commands: [APP_PATHS.PROFILE], queryParams: { screen: 'prescriptions' } }
        : { commands: [APP_PATHS.PROFILE] };

    case AppRoute.COMMUNITY:
      return resolveCommunityLink(sub, id, comment);

    case AppRoute.LIBRARY:
      return resolveLibraryLink(sub, id);

    case AppRoute.MEDICINE:
      return { commands: [APP_PATHS.MEDICINE] };

    case AppRoute.NOTIFICATIONS:
      // `NOTIFICATIONS_WEB_DEFAULT_PATH` — what the backend sends a push click to
      // when the row has no deep-link template of its own, i.e. the single most
      // common server-authored link there is. It resolved to `null` here until now,
      // which made the toast for every one of those pushes INERT: the one link the
      // server sends most often was the one the resolver refused.
      //
      // The inbox is the shell-owned bell PANEL, not a page, so this reproduces what
      // the `/notifications` ROUTE does (`primary.routes.ts`) rather than inventing a
      // second answer — Home, with the panel asked to open. Spelled as a literal
      // '1' because query params are strings on the wire and the shell compares them
      // as such.
      return { commands: [APP_PATHS.HOME], queryParams: { notifications: '1' } };

    case AppRoute.ORG:
      return resolveOrgLink(sub);

    default:
      return null; // unknown section → the caller decides (coarse route, or no-op)
  }
}

/**
 * `/library/article/98` → the canonical library reader route.
 *
 * These are REAL routes (`library.routes.ts` mounts `article/:id`, `podcast/:id`,
 * `meditation/:id`), which is why they resolve to a path rather than to the
 * Community `?screen=player` alias that `/community/articles/{id}` produces. Both
 * shapes end on the same reader; this one is the shareable url, so a notification
 * that names it keeps the url the recipient would also get from a share.
 *
 * The id is validated with the SAME predicate the route's own `canMatch` uses
 * ({@link isShareableId}), so a link this resolver accepts can never be one the
 * router then declines — which would put the reader on the branded 404 from a
 * notification that looked fine.
 */
function resolveLibraryLink(sub: string, id: string): DeepLinkRoute {
  const library = [APP_PATHS.LIBRARY];
  const kind =
    sub === ShareResource.ARTICLE || sub === 'articles'
      ? ShareResource.ARTICLE
      : sub === ShareResource.PODCAST || sub === 'podcasts'
        ? ShareResource.PODCAST
        : sub === ShareResource.MEDITATION || sub === 'meditations'
          ? ShareResource.MEDITATION
          : null;
  if (!kind || !isShareableId(id)) return { commands: library };
  return { commands: [...library, kind, id] };
}

/**
 * `/org/programs` → the B2B organization surfaces.
 *
 * No id anywhere: these three are full screens, not entity pages. A non-member's
 * `/org/...` stops MATCHING at the route's `canMatch` and lands on the branded 404
 * — which is the correct outcome for a notification they should not have received,
 * and is decided by the guard rather than second-guessed here.
 */
function resolveOrgLink(sub: string): DeepLinkRoute {
  switch (sub) {
    case 'counseling':
      return { commands: [APP_PATHS.ORG_COUNSELING] };
    case 'guardian':
      return { commands: [APP_PATHS.ORG_GUARDIAN] };
    case 'programs':
    default:
      return { commands: [APP_PATHS.ORG_PROGRAMS] };
  }
}

/**
 * `/v2/care/...` → the Care `?screen=` alias (+ the id its host reads).
 *
 * Every sub-path the backend's `config/notifications.deep_links` can emit is mapped,
 * and the id is carried for the three entities with a REAL by-id read behind them —
 * `sessions` (`GET /sessions/{id}`, CR16 Gap 4), `programs` and `groups`. Anything
 * else lands on the list it belongs to rather than the Care hub, which is where an
 * unmapped sub-path used to dump the viewer.
 *
 * Note this is the resolver used by the GLOBAL bell panel and the Community feed.
 * Care's own CR16 intercepts `/v2/care/...` before it reaches here so it can stay
 * IN-surface (its back arrow must return to the notification list); the destinations
 * the two produce are otherwise the same, which is the point — one row must not
 * behave differently depending on which surface it was tapped from.
 */
function resolveCareLink(sub: string, id: string): DeepLinkRoute {
  const care = [APP_PATHS.CARE];
  const at = (screen: string, param?: string): DeepLinkRoute => ({
    commands: care,
    queryParams: param && id ? { screen, [param]: id } : { screen },
  });
  switch (sub) {
    case 'sessions':
    case 'reservations':
    case 'reservation':
    case 'session':
    case 'appointments':
    case 'appointment':
      return at('sessions', 'session');
    case 'programs':
    case 'program':
      return at('programs', 'program');
    case 'groups':
    case 'group':
    case 'seminars':
    case 'seminar':
      return at('groups', 'group');
    case 'assessments':
    case 'assessment':
      // No by-id assessment deep-link: the id in `/v2/care/assessments/{id}` names an
      // instrument, and opening a RUN unasked would start a clinical questionnaire the
      // notification never asked for. The library is the honest destination.
      return at('assessments');
    case 'crisis':
    case 'emergency':
    case 'urgent':
      return at('crisis');
    case 'conversations':
    case 'messages':
    case 'chats':
      return at('conversations');
    case 'providers':
    case 'provider':
    case 'doctors':
    case 'directory':
      return at('directory');
    case 'enrollments':
    case 'mine':
      return at('mine');
    case '':
      return { commands: care };
    default:
      // An unknown sub-path is NOT guessed at — the Care hub is the honest landing.
      return { commands: care };
  }
}

/** `/v2/community/...` → the COMM `?screen=` alias (+ the ids the host reads). */
function resolveCommunityLink(sub: string, id: string, comment = ''): DeepLinkRoute {
  const base = [APP_PATHS.COMMUNITY];
  switch (sub) {
    case 'posts':
    case 'post':
      // The host opens a real server id via `openPostById` when it isn't a seed post;
      // `?comment=` (a community notification's `comment_id`) rides along so the thread
      // scrolls to + highlights the comment the notification announced.
      if (!id) return { commands: base };
      return {
        commands: base,
        queryParams: comment ? { screen: 'post', post: id, comment } : { screen: 'post', post: id },
      };
    case 'notifications':
    case 'notifs':
      return { commands: base, queryParams: { screen: 'notifs' } };
    case 'members':
    case 'member': {
      // `/v2/community/members/{handle}` — the handle-keyed member page (COMM8). The
      // handle is the key `GET /members/{handle}` takes, so the host opens it directly.
      // Community handles are Arabic, so the segment arrives percent-encoded — decode it
      // back to the raw handle. Falls back to the feed with no handle (COMM8 has nothing
      // to render without one).
      const handle = decodeSegment(id);
      return handle ? { commands: base, queryParams: { screen: 'profile', handle } } : { commands: base };
    }
    case 'identity':
      return { commands: base, queryParams: { screen: 'identity' } };
    case 'profile':
      // Bare `/v2/community/profile` = the viewer's OWN identity (COMM5). A member page
      // always carries a handle and comes in under `members/{handle}` above.
      return { commands: base, queryParams: { screen: 'identity' } };
    case 'library':
      return { commands: base, queryParams: { screen: 'library' } };
    case 'articles':
    case 'article':
    case 'podcasts':
    case 'podcast':
    case 'meditations':
    case 'meditation': {
      // `/v2/community/articles/{id}` → the reader, which fetches `/library/{kind}/{id}`.
      if (!id) return { commands: base, queryParams: { screen: 'library' } };
      const kind = sub.startsWith('podcast') ? 'podcast' : sub.startsWith('meditation') ? 'meditation' : 'article';
      return { commands: base, queryParams: { screen: 'player', content: id, kind } };
    }
    default:
      return { commands: base };
  }
}

/**
 * Decode a percent-encoded path segment, tolerating a malformed one (a lone `%`
 * makes `decodeURIComponent` throw) by falling back to the raw text.
 */
function decodeSegment(seg: string): string {
  if (!seg) return '';
  try {
    return decodeURIComponent(seg).trim();
  } catch {
    return seg;
  }
}
